import { z } from "zod";
import { SocialPostType } from "../../types/models/campaign.models.types";
import {
  SocialCreatorType,
  SocialGender,
  SocialPlatforms,
} from "../../types/models/social.models.types";

function transformEmptyString(x: string) {
  return x.trim().length === 0 ? null : x;
}

export const zCampaignFormAdmin = z
  .object({
    name: z.string().min(1, { message: "Campaign name is required" }),
    coverImage: z
      .object({
        id: z.string(),
        url: z.string(),
      })
      .nullable()
      .default(null),
    coverImageFile: z
      .array(
        z.object({
          file: z.instanceof(File),
        }),
      )
      .default([]),
    enableFilter: z.boolean().default(false),
    socialPostType: z.nativeEnum(SocialPostType, {
      required_error: "Post type is required",
    }).nullable().default(null),
    socialCreatorType: z
      .nativeEnum(SocialCreatorType)
      .nullable()
      .default(SocialCreatorType.meme),
    genderPreference: z.nativeEnum(SocialGender).nullable().default(null),
    socialPlatform: z.nativeEnum(SocialPlatforms).nullable().default(null),
    favouriteSocial: z.string().nullable().default(null),
    genre: z
      .array(
        z.object({
          id: z.string(),
          title: z.string(),
          emoji: z.string(),
        }),
      )
      .default([]),
    minimumFollowers: z.coerce.number().nullable().default(null),
    maximumFollowers: z.coerce.number().nullable().default(null),
    locationPreference: z
      .array(
        z.object({
          placeId: z.string(),
          address: z.string(),
        }),
      )
      .default([]),
    startDate: z
      .date()
      .refine(
        (date) => {
          if (!date) return true;
          return true;
        },
        { message: "Start date must be in the future" },
      )
      .nullable()
      .default(null),
    endDate: z.date().nullable().default(null),
    budget: z.coerce.number().min(1).nullable().default(null),
    budgetInternal: z.coerce.number().min(1).nullable().default(null),
    productRefund: z
      .object({
        image: z
          .array(
            z.object({
              id: z.string(),
              url: z.string(),
              name: z.string(),
            }),
          )
          .default([]),
        productImageFile: z.array(z.instanceof(File)).default([]),

        link: z.string().nullable().default(null),
        couponCode: z
          .string()
          .min(1, "Coupon code must be at least 1 character long")
          .optional()
          .nullable()
          .default(null),
        refundAmount: z.coerce.number().nullable().default(null),
        enabled: z.boolean().default(false),
      })
      .nullable()
      .default(null)
      .superRefine((data, ctx) => {
        if (!data) return;

        if (data.enabled) {
          // Check if `image` is null or empty
          if (data.image.length === 0 && data.productImageFile.length === 0) {
            ctx.addIssue({
              path: ["productImageFile"],
              message: "Product image is required",
              code: z.ZodIssueCode.custom,
            });
          }

          // Check if `link` is null or empty
          if (!data.link || data.link.trim() === "") {
            ctx.addIssue({
              path: ["link"],
              message: "Link is required",
              code: z.ZodIssueCode.custom,
            });
          }
        }
      }),

    links: z
      .array(
        z.object({
          title: z.string().min(1),
          url: z.string().url(),
        }),
      )
      .default([]),
    milestones: z.array(
      z.object({
        count: z.coerce.number(),
        payout: z.coerce.number().min(1),
      }),
    ),
    milestoneHasRefund: z.boolean().default(false),
    hasFixedPayout: z.boolean().default(false),
    assets: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
        }),
      )
      .default([]),
    assetFile: z.array(z.instanceof(File)).default([]),
    sampleVideos: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
        }),
      )
      .default([]),
    sampleVideosFiles: z.array(z.instanceof(File)).default([]),
    briefVideos: z
      .array(
        z.object({
          id: z.string(),
          url: z.string(),
        }),
      )
      .default([]),
    briefVideosFiles: z.array(z.instanceof(File)).default([]),
    hashtags: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    mentions: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    aboutCampaign: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    do: z.string().transform(transformEmptyString).nullable().default(null),
    dont: z.string().transform(transformEmptyString).nullable().default(null),
    aboutPayment: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
    aboutBrand: z
      .string()
      .transform(transformEmptyString)
      .nullable()
      .default(null),
  })
  .refine(
    (data) => {
      if (!data.startDate || !data.endDate) return true;
      return data.endDate > data.startDate;
    },
    {
      message: "End date must be after start date",
      path: ["endDate"],
    },
  )
  .refine(
    (data) => {
      if (data.budget !== null && data.budgetInternal !== null) {
        return Number(data.budget) <= Number(data.budgetInternal);
      }
      return true;
    },
    {
      message: "Budget cannot exceed internal budget",
      path: ["budget"],
    },
  );

export type IzCampaignFormAdmin = z.infer<typeof zCampaignFormAdmin>;
